import { router, usePage } from "@inertiajs/react";
import { apiPushDevicePath, apiPushDevicesPath } from "@travily/routes";
import { showToast } from "@ui/components/Toast";
import { useEffect, useRef } from "react";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { isNativePlatform } from "@/lib/native";
import type { SharedProps } from "@/types";

interface RegistrationToken {
  value: string;
}

interface PushNotification {
  title?: string;
  body?: string;
  data?: { url?: string };
}

interface ActionPerformed {
  notification: PushNotification;
}

export function usePushNotifications() {
  const { current_user } = usePage<SharedProps>().props;
  const userId = current_user?.id;
  const registeredTokenRef = useRef<string | null>(null);
  const csrfToken = useCsrfToken();

  useEffect(() => {
    if (!isNativePlatform() || !userId) return;

    let mounted = true;
    const cleanups: Array<() => void | Promise<void>> = [];

    (async () => {
      const [{ Capacitor }, { PushNotifications }] = await Promise.all([
        import("@capacitor/core"),
        import("@capacitor/push-notifications"),
      ]);

      if (!mounted) return;

      const registration = await PushNotifications.addListener("registration", async (token: RegistrationToken) => {
        if (registeredTokenRef.current === token.value) return;
        registeredTokenRef.current = token.value;

        await fetch(apiPushDevicesPath(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({ token: token.value, platform: Capacitor.getPlatform() }),
        });
      });

      const registrationError = await PushNotifications.addListener("registrationError", (error) => {
        console.error("Push registration error:", error);
      });

      const received = await PushNotifications.addListener(
        "pushNotificationReceived",
        (notification: PushNotification) => {
          const url = notification.data?.url;
          showToast.info(notification.title ?? "Travily", notification.body ?? "", {
            action: url ? { label: "View", onPress: () => router.visit(url) } : undefined,
          });
        },
      );

      const actionPerformed = await PushNotifications.addListener(
        "pushNotificationActionPerformed",
        ({ notification }: ActionPerformed) => {
          const url = notification.data?.url;
          if (url) router.visit(url);
        },
      );

      cleanups.push(() => registration.remove());
      cleanups.push(() => registrationError.remove());
      cleanups.push(() => received.remove());
      cleanups.push(() => actionPerformed.remove());

      const permission = await PushNotifications.checkPermissions();
      const granted =
        permission.receive === "granted" ||
        (permission.receive === "prompt" && (await PushNotifications.requestPermissions()).receive === "granted");

      if (granted) await PushNotifications.register();
    })();

    return () => {
      mounted = false;
      for (const cleanup of cleanups) cleanup();

      const token = registeredTokenRef.current;
      registeredTokenRef.current = null;
      if (!token) return;

      fetch(apiPushDevicePath(token), {
        method: "DELETE",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken },
      }).catch(() => {});
    };
  }, [userId, csrfToken]);
}
